import { FieldNames, FormType } from "@/enums";
import { ISelectOption } from "@/interfaces";
import { IProjectTeam } from "@/interfaces/Project/Roles/IProjectTeam";
import { IRegionalProjectRole } from "@/interfaces/Project/Roles/IRegionalProjectRole";
import { useFormPropertyValueLookupProvider } from "@/providers";
import { useGetFormLookupsQuery } from "@/services";
import { Form, Modal, Select } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { v4 } from "uuid";

interface IProps {
  regionRole: IRegionalProjectRole;
  open: boolean;
  onClose(): void;
  onOk(data: IProjectTeam): void;
  projectTeamId?: string;
}

export const AddEditProjectTeamModal = ({
  regionRole,
  open,
  onClose,
  onOk,
  projectTeamId,
}: IProps) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [projectTypes, setProjectTypes] = useState<ISelectOption[]>([]);
  const [productCategories, setProductCategories] = useState<ISelectOption[]>(
    []
  );
  const [workingProjectTeam, setWorkingProjectTeam] = useState<IProjectTeam>();

  const { data: forms } = useGetFormLookupsQuery();

  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();

  useEffect(() => {
    setWorkingProjectTeam(
      regionRole.projectTeams.find((x) => x.id === projectTeamId)
    );
  }, [projectTeamId, regionRole.projectTeams]);

  useEffect(() => {
    const intake = forms?.intake;
    if (!intake) return;

    setProjectTypes(
      Object.keys(intake?.projectType).map((key) => ({
        value: key,
        label: getPropertyValueDisplay(
          FormType.Intake,
          FieldNames.ProjectType,
          key
        ),
      }))
    );

    setProductCategories(
      Object.keys(intake?.productCategory).map((key) => ({
        value: key,
        label: getPropertyValueDisplay(
          FormType.Intake,
          FieldNames.ProductCategory,
          key
        ),
      }))
    );
  }, [forms?.intake, getPropertyValueDisplay]);
  const handleAdd = async (): Promise<void> => {
     await form.validateFields().then(
      (data: {
       publicProjectTypeId: string;
       publicProductCategoryIds: string[];
      }) => {
       const id = workingProjectTeam?.id ?? v4();

       onOk({
        id,
        publicId: id,
        ...data,
       });

       form.resetFields();
      }
     );
  };

  const isDuplicateProjectType = (value: string): boolean =>
    regionRole.projectTeams.some(
     (team) => team.publicProjectTypeId === value && team.id !== workingProjectTeam?.id
    );

  return (
    <Modal
      centered
      closable={false}
      title={intl.formatMessage({
        id: workingProjectTeam?.id
          ? "projectRoles.editProjectTeam"
          : "projectRoles.addProjectTeam",
      })}
      open={open}
      cancelText={intl.formatMessage({ id: "general.cancel" })}
      okText={intl.formatMessage({
        id: workingProjectTeam?.id ? "general.ok" : "general.add",
      })}
      onCancel={onClose}
      onOk={handleAdd}
      destroyOnHidden={true}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="publicProjectTypeId"
          initialValue = { workingProjectTeam?.publicProjectTypeId }
          label={intl.formatMessage({ id: "projectRoles.projectType" })}
          rules={[
            { required: true, message: "" },
            {
              validator(_, value) {
                if (value && isDuplicateProjectType(value)) {
                  return Promise.reject(
                    new Error(
                      intl.formatMessage({
                        id: "projectRoles.duplicateProjectTypeError",
                      })
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Select options={projectTypes} />
        </Form.Item>
        <Form.Item
          name="publicProductCategoryIds"
          label={intl.formatMessage({ id: "projectRoles.productCategory" })}
          rules={[{ required: true, message: "" }]}
          initialValue = { workingProjectTeam?.publicProductCategoryIds }
        >
          <Select allowClear mode="multiple" options={productCategories} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
